import { useInfiniteQuery } from '@tanstack/react-query';
import type { AgentApiCompaniesIndex } from '@/types/serializers';
import type { CustomFieldContent } from '../../candidates/api/useFetchCandidateProfiles';

export type CustomFieldQuery = {
  field_id: number;
  content: CustomFieldContent;
};

export type Request = {
  query?: string;
  industry_id?: string;
  listed_phase?: string;
  sort?: string;
  address?: string;
  description?: string;
  organization_user_id?: string;
  number_of_employees?: {
    min?: number;
    max?: number;
  };
  established_date?: {
    min?: number;
    max?: number;
  };
  custom_fields?: CustomFieldQuery[];
  custom_tag_ids?: number[];
  custom_tag_match?: 'all' | 'any';
  page?: number;
};

const buildQueryParams = (query?: Request): string => {
  if (!query) return '';

  const params: string[] = [];

  if (query.query) {
    params.push(`query=${encodeURIComponent(query.query)}`);
  }

  if (query.industry_id) {
    params.push(`industry_id=${encodeURIComponent(query.industry_id)}`);
  }

  if (query.listed_phase) {
    params.push(`listed_phase=${encodeURIComponent(query.listed_phase)}`);
  }

  if (query.sort) {
    params.push(`sort=${encodeURIComponent(query.sort)}`);
  }

  if (query.address) {
    params.push(`address=${encodeURIComponent(query.address)}`);
  }

  if (query.description) {
    params.push(`description=${encodeURIComponent(query.description)}`);
  }

  if (query.organization_user_id) {
    params.push(`organization_user_id=${encodeURIComponent(query.organization_user_id)}`);
  }

  if (query.number_of_employees) {
    if (query.number_of_employees.min !== undefined && query.number_of_employees.min !== 0) {
      params.push(`number_of_employees[min]=${query.number_of_employees.min}`);
    }
    if (query.number_of_employees.max !== undefined && query.number_of_employees.max !== 0) {
      params.push(`number_of_employees[max]=${query.number_of_employees.max}`);
    }
  }

  if (query.established_date) {
    if (query.established_date.min !== undefined && query.established_date.min !== 0) {
      params.push(`established_date[min]=${query.established_date.min}`);
    }
    if (query.established_date.max !== undefined && query.established_date.max !== 0) {
      params.push(`established_date[max]=${query.established_date.max}`);
    }
  }

  if (query.custom_tag_ids && query.custom_tag_ids.length > 0) {
    query.custom_tag_ids.forEach((id) => {
      params.push(`custom_tag_ids[]=${id}`);
    });
  }

  if (query.custom_tag_match === 'any') {
    params.push(`custom_tag_match=any`);
  }

  if (query.custom_fields && query.custom_fields.length > 0) {
    query.custom_fields.forEach((field) => {
      params.push(`custom_fields[][field_id]=${field.field_id}`);

      if (Array.isArray(field.content)) {
        field.content.forEach((value) => {
          params.push(`custom_fields[][content][]=${encodeURIComponent(String(value))}`);
        });
      } else if (typeof field.content === 'object' && field.content !== null) {
        if (field.content.min !== undefined) {
          params.push(`custom_fields[][content][min]=${field.content.min}`);
        }
        if (field.content.max !== undefined) {
          params.push(`custom_fields[][content][max]=${field.content.max}`);
        }
      } else {
        params.push(`custom_fields[][content]=${encodeURIComponent(String(field.content))}`);
      }
    });
  }

  if (query.page !== undefined) {
    params.push(`page=${query.page}`);
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
};

export default function useFetchCompanies(query?: Request) {
  const queryKey = ['/agent/api/companies', JSON.stringify(query)];

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const queryWithPage = query ? { ...query, page: pageParam } : { page: pageParam };
      const queryString = buildQueryParams(queryWithPage);
      const response = await fetch(`/agent/api/companies.json${queryString}`);
      const data: AgentApiCompaniesIndex = await response.json();
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.is_last_page) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
}
